import Konva from 'konva';
import _ from 'lodash';
import { uuid2 as uuid } from '@/utils';
import util from './util';
import GraphicFactory from './graphic';
import Point from './point';
import * as UserUrl from './assets/user.png';

const VIEW = {
  SELECTION: 'selection',
  PATH: 'path',
  POLYGON: 'polygon',
  POINT: 'point',
  BLOCKPOINT: 'blockpoint',
  SELFAREA: 'selfArea',
};

class KonvaStage {
  constructor(payload) {
    this.payload = payload;

    const { container } = this.payload;
    const { clientWidth, clientHeight } = container;

    this.stage = new Konva.Stage({
      container,
      width: clientWidth,
      height: clientHeight,
      draggable: true,
    });

    this.svgLayer = new Konva.Layer();
    this.textLayer = new Konva.Layer(); // 存放路网的文字
    this.pointLayer = new Konva.Layer();
    this.lineLayer = new Konva.Layer();
    this.areaLayer = new Konva.Layer();
    this.tmpLayer = new Konva.Layer();
    this.dragLayer = new Konva.Layer();

    this.stage.add(this.svgLayer);
    this.stage.add(this.areaLayer);
    this.stage.add(this.tmpLayer);
    this.stage.add(this.lineLayer);
    this.stage.add(this.pointLayer);
    this.stage.add(this.textLayer);
    this.stage.add(this.dragLayer);

    this.areaLayer.draw();
    this.pointLayer.draw();
    this.lineLayer.draw();
    this.svgLayer.draw();
    this.tmpLayer.draw();

    // 缩放和位移相关
    this.graphicPosition = { x: 0, y: 0 };
    this.graphicScale = 1;
    this.graphicDelay = 500;
    this.dragTimer = 0;

    this.tr = new Konva.Transformer({
      resizeEnabled: false,
      rotateEnabled: false,
    });
    this.pointLayer.add(this.tr);
    this.lineLayer.add(this.tr);

    let imgWidth = 4000;
    let imgHeight = 4000;
    if (this.payload.svgAlignParam) {
      imgWidth = this.payload.svgAlignParam.img_width;
      imgHeight = this.payload.svgAlignParam.img_height;
    }
    // scale 大小
    const scale = Math.max(clientWidth / imgWidth, clientHeight / imgHeight);
    this.stage.scale({ x: scale, y: scale });

    // 鼠标点，跟随鼠标移动
    this.mousePoint = new Konva.Circle({
      id: -1,
      radius: 5,
      fill: 'blue',
    });
    this.pointLayer.add(this.mousePoint);

    const userImage = new Image();
    userImage.src = UserUrl.default;

    let userPointWidth = 30;
    if (this.payload.config && this.payload.config.panorama) {
      userPointWidth = this.payload.config.panorama.width;
    }

    // 用户点
    this.userPoint = new Konva.Image({
      image: userImage,
      x: 0,
      y: 0,
      width: userPointWidth,
      height: userPointWidth,
      offsetX: userPointWidth / 2,
      offsetY: userPointWidth / 2,
      visible: false,
    });
    this.tmpLayer.add(this.userPoint);

    // 当前的点
    this.prevPoint = null;
    this.prevLine = null;
    this.curPoints = null;
    this.curShape = null;
    this.snapLine = null;
    // 坐标点集合
    this.pointDict = {};
    // 图形集合
    this.graphicList = [];
    this.svgShapes = [];

    this.pois = [];
    // 拖拽的节点
    this.draggingLines = [];

    this.rightClickCount = 0;

    this.events = {
      keyboardHandler: (e) => {
        this.keyboardHandler(e);
      },
    };

    this.initSvg();
    this.initEvents();
  }

  // calc3dTo2d(mat, pos) {
  //   const v4 = vec4.create();
  //   vec4.set(v4, x, y, z, 1);

  //   const m4 = mat4.fromValues(...downTrans);

  //   const rs = vec4.create();
  //   vec4.transformMat4(rs, v4, m4);
  //   return { x: rs[0], y: rs[1] };

  // }

  static getDistance(screen, p1, p2) {
    return Math.abs(p1.x - p2.x) < screen.clientWidth / 2 && Math.abs(p1.y - p2.y) < screen.clientHeight / 2;
  }

  initEvents() {
    const { stage } = this;
    // 舞台滚动放大缩小
    stage.on('wheel', (e) => {
      this.setGraphicPosition(e);
      stage.scale({ x: this.graphicScale, y: this.graphicScale });
      stage.position(this.graphicPosition);
      // 缩放时隐藏掉屏幕外的节点和线段
      const now = Date.now();
      if ((now - this.dragTimer) > this.graphicDelay) {
        this.dragTimer = now;
        this.setGraphicPointVisible();
      }
      stage.batchDraw();
    });

    stage.on('dragmove', (e) => {
      if (!(e.target instanceof Konva.Stage)) return;

      const now = Date.now();
      if ((now - this.dragTimer) > this.graphicDelay) {
        this.dragTimer = now;
        this.setGraphicPosition(e);
        // 缩放过小时，拖动时隐藏节点，增加流畅度
        const hideTypes = this.graphicScale < 1 ? ['point'] : [];
        this.setGraphicPointVisible(hideTypes);
        stage.batchDraw();
      }
    });

    stage.on('dragend', (e) => {
      this.setGraphicPosition(e);
      this.setGraphicPointVisible();
      stage.batchDraw();
    });

    // 鼠标移动
    stage.on('mousemove', (e) => {
      if (this.view === VIEW.SELECTION) return;
      const { x: ox, y: oy } = this.getWorldPosition(e);
      const { x, y } = this.snapPoint(ox, oy);

      if (this.mousePoint) {
        this.mousePoint.x(x);
        this.mousePoint.y(y);
        this.pointLayer.batchDraw();
      }

      if (this.view === VIEW.PATH) {
        if (this.prevPoint) {
          if (!this.prevLine) {
            this.prevLine = new Konva.Line({
              stroke: 'red',
              lineCap: 'round',
              strokeWidth: 4,
            });
            this.lineLayer.add(this.prevLine);
          }
          this.prevLine.points([this.prevPoint.x, this.prevPoint.y, x, y]);
          this.lineLayer.batchDraw();
        }
      }
      if (this.view === VIEW.POLYGON) {
        if (this.prevPoint) {
          if (!this.prevLine) {
            this.prevLine = new Konva.Line({
              stroke: 'blue',
              strokeWidth: 4,
              closed: true,
            });
            this.lineLayer.add(this.prevLine);
          }
          const res = [];
          this.curPoints.forEach((p) => {
            res.push(p.x, p.y);
          });
          this.prevLine.points([this.prevPoint.x, this.prevPoint.y, x, y]);
          this.lineLayer.batchDraw();
        }
      }
      if (this.view === VIEW.SELFAREA) {
        if (this.prevPoint) {
          if (!this.prevLine) {
            this.prevLine = new Konva.Line({
              stroke: 'blue',
              strokeWidth: 4,
              closed: false,
            });
            this.areaLayer.add(this.prevLine);
          }
          const res = [];
          this.curPoints.forEach((p) => {
            res.push(p.x, p.y);
          });
          this.prevLine.points([...res, x, y]);
          this.areaLayer.batchDraw();
        }
      }
    });

    stage.on('mouseup', (e) => {
      if (e.evt.button !== 2) {
        this.rightClickCount = 0;
      }
    });

    // 点击
    stage.on('click', (e) => {
      if (this.view === VIEW.POINT || this.view === VIEW.BLOCKPOINT) {
        // 右键点击
        if (e.evt.button === 2) {
          this.prevPoint = null;
          this.changeView(VIEW.SELECTION);
          return;
        }

        // 如果点击的是空位
        if (e.target.id() === -1) {
          this.addPoint();
        }
      }
      if (this.view === VIEW.PATH) {
        // 右键点击
        if (e.evt.button === 2) {
          this.prevPoint = null;

          if (this.prevLine) this.prevLine.destroy();
          this.prevLine = null;

          this.lineLayer.batchDraw();
          return;
        }

        // 如果点击的是空位
        if (e.target.id() === -1) {
          this.addPoint();
        } else {
          // 如果点击的是点位
          const id = e.target.attrs.pid;
          if (!id) return;
          if (!e.target.attrs.isPoint) return;
          if (this.prevPoint) {
            if (this.prevPoint.id === id) return;
            this.addGraphic('line', [this.prevPoint.id, id], {}, uuid(), 'LinePoint');
          } else {
            this.prevPoint = this.pointDict[id];
          }
          this.render();
        }
      }
      if (this.view === VIEW.POLYGON) {
        // 右键点击
        if (e.evt.button === 2) {
          if (this.curPoints && this.curPoints.length > 1) {
            this.addGraphic('polygon', this.curPoints.map(c => c.id), {}, uuid(), 'PolygonPoint');
          }

          this.prevPoint = null;
          this.curPoints = null;

          if (this.prevLine) this.prevLine.destroy();
          this.prevLine = null;

          this.lineLayer.batchDraw();
          return;
        }

        // 如果点击的是空位
        if (e.target.id() === -1) {
          this.addPoint();
        } else {
          // 如果点击的是点位
          const id = e.target.id();
          if (!id) return;
          if (this.curPoints) {
            this.curPoints.push(this.pointDict[id]);
          } else {
            this.curPoints = [this.pointDict[id]];
          }
          this.prevPoint = this.pointDict[id];
          this.render();
        }
      }
      if (this.view === VIEW.SELFAREA) {
        // 右键点击
        if (e.evt.button === 2) {
          if (this.curPoints && this.curPoints.length > 3) {
            const p0 = this.curPoints[0]; // 起点
            const pLast = this.curPoints[this.curPoints.length - 1]; // 终点
            const dx = Math.abs(p0.x - pLast.x);
            const dy = Math.abs(p0.y - pLast.y);
            const dis = Math.sqrt(Math.pow(dx, 2) + Math.pow(dy, 2));
            if (dis <= 10) {
              this.curPoints[this.curPoints.length - 1].x = p0.x;
              this.curPoints[this.curPoints.length - 1].y = p0.y;
            }
            const property = { type: '0', show_type: '0', name: '', image_url: '' };
            this.addGraphic('area', this.curPoints.map(c => c.id), property, uuid(), 'selfArea');
          }
          this.prevPoint = null;
          this.curPoints = null;
          if (this.prevLine) this.prevLine.destroy();
          this.prevLine = null;

          this.areaLayer.batchDraw();
          return;
        }

        // 如果点击的是空位
        if (e.target.id() === -1) {
          this.addPoint();
        }
      }

      this.updateCurShape(null);
    });

    // 右键
    stage.on('contentContextmenu', (e) => {
      e.evt.preventDefault();
      e.evt.stopPropagation();
      this.rightClickCount += 1;
      if (this.rightClickCount === 2) {
        this.changeView(VIEW.SELECTION);
      }
    });

    // 键盘
    window.addEventListener('keydown', this.events.keyboardHandler);
  }

  // 计算画布的当前坐标和缩放值
  setGraphicPosition(e) {
    const { stage } = this;
    const scaleBy = 1.05;

    e.evt.preventDefault();
    const oldScale = stage.scaleX();
    const pointer = stage.getPointerPosition();

    const mousePointTo = {
      x: (pointer.x - stage.x()) / oldScale,
      y: (pointer.y - stage.y()) / oldScale,
    };

    const newScale =
      e.evt.deltaY < 0 ? oldScale * scaleBy : oldScale / scaleBy;

    this.graphicPosition = {
      x: pointer.x - mousePointTo.x * newScale,
      y: pointer.y - mousePointTo.y * newScale,
    };
    this.graphicScale = newScale;
  }

  // 设置地图点位显示、隐藏，优化流畅度
  setGraphicPointVisible(hideTypes = []) {
    const { stage } = this;
    const centerPosition = {
      x: stage.container().clientWidth / 2 - this.graphicPosition.x,
      y: stage.container().clientHeight / 2 - this.graphicPosition.y,
    };

    this.graphicList.forEach((g) => {
      if (hideTypes.includes(g.type)) {
        g.ele.hide();
        return;
      }
      let isOnScreen = true;
      const nodePosition = [];

      nodePosition.push({
        x: this.pointDict[g.data[0]].x * this.graphicScale,
        y: this.pointDict[g.data[0]].y * this.graphicScale,
      });
      // 线段检测两边的点
      if (g.type === 'line') {
        nodePosition.push({
          x: this.pointDict[g.data[1]].x * this.graphicScale,
          y: this.pointDict[g.data[1]].y * this.graphicScale,
        });
        isOnScreen = KonvaStage.getDistance(stage.container(), nodePosition[0], centerPosition) ||
          KonvaStage.getDistance(stage.container(), nodePosition[1], centerPosition);
      } else {
        isOnScreen = KonvaStage.getDistance(stage.container(), nodePosition[0], centerPosition);
      }
      // 过滤通行方向
      if (!g.property.path_direction) {
        if (isOnScreen) {
          g.ele.show();
        } else {
          g.ele.hide();
        }
      }
    });
  }

  keyboardHandler(e) {
    const tag = document.activeElement;
    if (tag.tagName === 'INPUT') return;
    const [point] = this.tr.nodes();
    if (!point) return;
    if (!point.attrs.draggable) return;
    if (['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].indexOf(e.key) === -1) return;
    this.updateCurShape(null);
    if (e.key === 'ArrowLeft') {
      point.x(point.x() - 1);
    }
    if (e.key === 'ArrowRight') {
      point.x(point.x() + 1);
    }
    if (e.key === 'ArrowUp') {
      point.y(point.y() - 1);
    }
    if (e.key === 'ArrowDown') {
      point.y(point.y() + 1);
    }

    const graphic = this.graphicList.find(g => g.id === point.attrs.id);
    graphic.property.block_id = '';
    this.pointDict[point.attrs.id].x = point.x();
    this.pointDict[point.attrs.id].y = point.y();

    this.graphicList.forEach((g) => {
      if (g.type === 'line') {
        if (!g.data.find(i => i === graphic.id)) return;
        g.updateEle();
      }
      if (g.type === 'polygon') {
        const pIndex = g.data.findIndex(i => i === graphic.id);
        if (pIndex === -1) return;
        const points = g.ele.points();
        points[pIndex * 2] = this.pointDict[g.data[pIndex]].x;
        points[pIndex * 2 + 1] = this.pointDict[g.data[pIndex]].y;
        g.ele.points(points);
      }
      if (this.payload.listeners.graphicChanged) {
        this.payload.listeners.graphicChanged(g.toObject());
      }
    });

    this.changeTransformNodes(point);
    if (this.payload.listeners.graphicChanged) {
      this.payload.listeners.graphicChanged(graphic.toObject());
    }

    this.render();
  }

  changeTransformNodes(node) {
    if (this.prevNode && this.prevNode.graphic) {
      this.prevNode.graphic.setSelected(false);
    }
    if (!node) {
      this.tr.nodes([]);
    } else {
      this.prevNode = node;
      if (this.prevNode && this.prevNode.graphic) {
        this.prevNode.graphic.setSelected(true);
      }
      this.tr.nodes([node]);
    }
  }

  updateUserPoint(userData) {
    if (userData.visible === true) {
      this.userPoint.show();
    } else if (userData.visible === false) {
      this.userPoint.hide();
    }
    if (userData.rotation) {
      this.userPoint.rotation(userData.rotation);
    }
    if (userData.x) {
      this.userPoint.x(userData.x);
    }
    if (userData.y) {
      this.userPoint.y(userData.y);
    }

    this.render();
  }

  drawErrorPaths(arr) {
    this.graphicList.forEach((g) => {
      if (arr.indexOf(g.id) > -1) {
        g.ele.children[0].stroke('red');
      }
    });
    this.lineLayer.batchDraw();
  }

  drawErrorPoints(arr) {
    this.graphicList.forEach((g) => {
      if (arr.indexOf(g.id) > -1) {
        g.ele.stroke('red');
      }
    });
    this.pointLayer.batchDraw();
  }


  setCenter(pos) {
    const x = this.stage.width() / 2 - pos.x * this.stage.scale().x;
    const y = this.stage.height() / 2 - pos.y * this.stage.scale().y;
    this.stage.position({ x, y });
    this.stage.batchDraw();
  }

  pointFocus(point) {
    const pos = this.pointDict[point.graphic_id];
    const x = pos.x;
    const y = pos.y;
    this.stage.scale({ x: 2.5, y: 2.5 });
    this.setCenter({ x, y });
  }

  getWorldPosition(e) {
    const { top, left } = this.payload.container.getBoundingClientRect();
    const scale = this.stage.scaleX();
    const offsetX = this.stage.x();
    const offsetY = this.stage.y();
    return {
      x: (e.evt.x - offsetX - left) / scale,
      y: (e.evt.y - offsetY - top) / scale,
    };
  }

  addPoint(e) {
    this.changeTransformNodes(null);

    const x = this.mousePoint.x();
    const y = this.mousePoint.y();

    const point = new Point(null, x, y);
    console.log(point);
    this.pointDict[point.id] = point;

    // 增加块级点
    if (this.view === VIEW.BLOCKPOINT) {
      const property = { type: '0' };
      this.addGraphic('blockPoint', [point.id], property, point.id, 'BlockPoint', point.uuid);
    }
    if (this.view === VIEW.PATH || this.view === VIEW.POINT) {
      const property = { type: '0' };
      if (this.curShape) {
        property.block_id = this.curShape.attrs.name;
      }
      this.addGraphic('point', [point.id], property, point.id, 'LinePoint', point.uuid);
      if (this.prevPoint && this.view === VIEW.PATH) {
        this.addGraphic('line', [this.prevPoint.id, point.id], {}, uuid(), 'LinePoint');
      }
      // 如果点到线上，将原来的线分割
      if (this.snapLine) {
        this.addGraphic('line', [this.snapLine.data[0], point.uuid], {}, uuid(), 'LinePoint');
        this.addGraphic('line', [point.uuid, this.snapLine.data[1]], {}, uuid(), 'LinePoint');
        this.deleteGraphic(this.snapLine.id);
        this.snapLine = null;
      }

      this.prevPoint = point;
    }
    if (this.view === VIEW.POLYGON) {
      const property = { type: '0' };
      if (this.curShape) {
        property.block_id = this.curShape.attrs.name;
      }
      this.addGraphic('point', [point.id], property, point.id, 'point');
      if (!this.curPoints) {
        this.curPoints = [];
      }
      this.curPoints.push(point);
      this.prevPoint = point;
    }
    if (this.view === VIEW.SELFAREA) {
      if (!this.curPoints) {
        this.curPoints = [];
      }
      this.curPoints.push(point);
      console.log(point);
      this.prevPoint = point;
    }
    this.render();
  }

  loadGraphics(data) {
    Object.entries(data.point_dict).forEach((key) => {
      if (!(key[1].uuid in this.pointDict)) {
        const point = new Point(key[1].uuid, key[1].x, key[1].y);
        this.pointDict[point.id] = point;
      }
    });
    data.graphic_list.forEach((g) => {
      if (!this.graphicList.find(t => t.id === g.graphic_id)) {
        this.addGraphic(g.graphic_type, g.graphic_data, g.property, g.group_id, g.group_type, g.graphic_id);
      }
    });
    this.render();
  }

  render() {
    this.pointLayer.batchDraw();
    this.lineLayer.batchDraw();
    this.areaLayer.batchDraw();
    this.tmpLayer.batchDraw();
  }

  snapPoint(x, y, id = null) {
    let min = Number.MAX_VALUE;
    let rx = x;
    let ry = y;
    const snapDistance = 70;

    // 计算与图形的吸附
    this.graphicList.forEach((g) => {
      if (g.type === 'point' && g.group_type !== 'PanoramaPoint') {
        if (id && g.data[0] === id) return;
        const lx = this.pointDict[g.data[0]].x;
        const ly = this.pointDict[g.data[0]].y;
        const distance = ((x - lx) ** 2) + ((y - ly) ** 2);
        if (distance < min) {
          min = distance;
          rx = lx;
          ry = ly;
        }
      }
    });

    if (min < snapDistance) {
      this.snapLine = null;
      this.updateCurShape(null);
      return { x: rx, y: ry };
    }

    // 计算与块级点的吸附
    let blockPointShape = null;
    this.graphicList.forEach((g) => {
      if (g.type === 'blockPoint') {
        if (id && g.data[0] === id) return;
        const lx = this.pointDict[g.data[0]].x;
        const ly = this.pointDict[g.data[0]].y;
        const distance = ((x - lx) ** 2) + ((y - ly) ** 2);
        if (distance < min) {
          min = distance;
          rx = lx;
          ry = ly;
          blockPointShape = g.ele;
        }
      }
    });

    if (min < snapDistance) {
      this.snapLine = null;
      this.updateCurShape(blockPointShape);
      return { x: rx, y: ry };
    }

    this.graphicList.forEach((g) => {
      if (g.type === 'line') {
        if (id && (g.data[0] === id || g.data[1] === id)) return;
        const from = this.pointDict[g.data[0]];
        const to = this.pointDict[g.data[1]];
        const res = util.distanceSquaredToLineSegment(from.x, from.y, to.x, to.y, x, y);
        if (res[0] < min) {
          min = res[0];
          rx = res[1].x;
          ry = res[1].y;
          this.snapLine = g;
        }
      }
    });

    // 计算与svg的吸附
    const res = util.closestPath(this.svgShapes, { x, y }, this.svgLayer);

    if (min < res.min) {
      this.updateCurShape(null);
      if (min < snapDistance) {
        return { x: rx, y: ry };
      }
      this.snapLine = null;
      return { x, y };
    }
    if (res.min < snapDistance) {
      this.updateCurShape(res.shape);
      this.snapLine = null;
      return { x: res.point[0], y: res.point[1] };
    }
    this.updateCurShape(null);
    this.snapLine = null;
    return { x, y };
  }

  updateGraphic(target) {
    if (!target.graphic_id) return;
    const graphic = this.graphicList.find(g => g.id === target.graphic_id);
    if (!graphic) return;

    graphic.update(target);
    this.render();
  }

  updateCurShape(shape) {
    if (!shape) {
      if (!this.curShape) return;
      this.curShape.destroy();
      this.curShape = null;
      this.tmpLayer.batchDraw();
      return;
    }

    if (shape instanceof Konva.Path) {
      if (this.curShape) {
        if (this.curShape.data && this.curShape.data() === shape.data()) return;
        this.curShape.attrs.name = shape.attrs.name;
        // eslint-disable-next-line no-unused-expressions
        this.curShape.data && this.curShape.data(shape.data());
        if (shape.attrs.properties) {
          this.curShape.setAttr('properties', shape.attrs.properties);
        }
      } else {
        this.curShape = new Konva.Path({
          name: shape.attrs.name,
          data: shape.data(),
          fill: 'rgba(255, 0, 0, 0.5)',
        });

        if (shape.attrs.properties) {
          this.curShape.setAttr('properties', shape.attrs.properties);
        }

        this.tmpLayer.add(this.curShape);
      }
    }

    if (shape instanceof Konva.Circle) {
      if (this.curShape) {
        this.curShape.destroy();
        this.curShape = null;
      }
      this.curShape = new Konva.Circle({
        name: shape.attrs.name,
        x: shape.x(),
        y: shape.y(),
        radius: shape.radius(),
        fill: 'rgba(255, 0, 0, 0.5)',
      });
      this.tmpLayer.add(this.curShape);
    }

    this.tmpLayer.batchDraw();
  }

  addGraphic(type, data = [], property = {}, group_id, group_type, id = null) {
    const graphic = GraphicFactory.buildGraphic({
      id,
      type,
      data,
      group_id,
      group_type,
      property,
    }, this);

    if (!graphic.ele) return null;

    if (this.view === VIEW.SELECTION) {
      if (graphic.canDraggable && graphic.ele) {
        graphic.ele.draggable(true);
      }
    }
    this.graphicList.push(graphic);

    if (this.payload.listeners.graphicChanged) {
      this.payload.listeners.graphicChanged(graphic.toObject());
    }

    return graphic;
  }

  pointDragStartHandler(e) {
    const id = e.currentTarget.attrs.pid;
    // 拖拽关联的线放到单独的层绘制
    this.graphicList.forEach((g) => {
      if (g.type === 'line') {
        if (!g.data.find(i => i === id)) return;
        this.dragLayer.add(g.ele);
        this.draggingLines.push(g);
      }
    });
    this.render();
    this.dragLayer.draw();

    // 事件
    if (this.payload.listeners.pointDragStart) {
      const data = this.graphicList.find(i => i.id === e.currentTarget.id());
      if (this.curShape && this.curShape.attrs && this.curShape.attrs.properties && this.curShape.attrs.properties.parking_no) {
        data.property.name = this.curShape.attrs.properties.parking_no;
      }
      this.payload.listeners.pointDragStart(
        data.toObject(),
        'graphic',
        this.getPointChange(e),
      );
    }
  }

  pointDragEndHandler(e) {
    // 拖动元素移动到原本的图层，数据清除
    const newLines = [];
    this.dragLayer.children.forEach((item) => {
      newLines.push(item);
    });
    if (newLines.length <= 0) return;
    this.lineLayer.add(...newLines);
    this.dragLayer.removeChildren();
    this.draggingLines = [];

    this.render();
    // 事件
    if (this.payload.listeners.pointDragStart) {
      const data = this.graphicList.find(i => i.id === e.currentTarget.id());
      if (this.curShape && this.curShape.attrs && this.curShape.attrs.properties && this.curShape.attrs.properties.parking_no) {
        data.property.name = this.curShape.attrs.properties.parking_no;
      }
      this.payload.listeners.pointDragStart(
        data.toObject(),
        'graphic',
        this.getPointChange(e),
      );
    }
  }

  pointDragHandler(e) {
    // 合并事件
    if (this.payload.listeners.graphicChanged) {
      this.payload.listeners.graphicChanged(this.getPointChange(e));
    }
  }

  // 获取点位变化
  getPointChange(e) {
    const { x: ox, y: oy } = this.getWorldPosition(e);
    const id = e.currentTarget.attrs.pid;
    const type = e.currentTarget.attrs.type;
    let x = ox;
    let y = oy;

    if (type !== 'blockPoint') {
      const xy = this.snapPoint(ox, oy, id);
      x = xy.x;
      y = xy.y;
    }

    this.pointDict[id].x = x;
    this.pointDict[id].y = y;
    e.currentTarget.x(x);
    e.currentTarget.y(y);
    this.changeTransformNodes(e.currentTarget);
    if (this.payload.listeners.pointClick) {
      const data = this.graphicList.find(i => i.id === e.currentTarget.id());
      if (this.curShape && this.curShape.attrs && this.curShape.attrs.properties && this.curShape.attrs.properties.parking_no) {
        data.property.name = this.curShape.attrs.properties.parking_no;
      }
      this.payload.listeners.pointClick(
        data.toObject(),
        'graphic',
        this.pointDict[id],
      );
    }
    const graphicChanges = {
      pointDict: {},
      graphicList: [],
    };
    this.graphicList.forEach((g) => {
      if (g.type === 'point') {
        if (g.data[0] !== id) return;
        if (this.curShape) {
          g.property.block_id = this.curShape.attrs.name;
        } else {
          g.property.block_id = '';
        }
        this.addGraphicChanges(graphicChanges, g);
      }
      if (g.type === 'line') {
        if (!g.data.find(i => i === id)) return;
        this.drawDraggingLines();
        this.addGraphicChanges(graphicChanges, g);
        graphicChanges.graphicList[0].updateText();
      }
      if (g.type === 'polygon') {
        const pIndex = g.data.findIndex(i => i === id);
        if (pIndex === -1) return;
        const points = g.ele.points();
        points[pIndex * 2] = this.pointDict[g.data[pIndex]].x;
        points[pIndex * 2 + 1] = this.pointDict[g.data[pIndex]].y;
        g.ele.points(points);
        this.addGraphicChanges(graphicChanges, g);
      }
    });

    // this.render();
    return this.exportData(graphicChanges);
  }

  addGraphicChanges(graphicChanges, g) {
    const { data } = g;
    data.forEach((gId) => {
      graphicChanges.pointDict[gId] = this.pointDict[gId];
    });
    graphicChanges.graphicList.push(g);
  }

  // 单独绘制拖拽的线
  drawDraggingLines() {
    this.draggingLines.forEach((g) => {
      g.updateEle();
    });
    this.dragLayer.batchDraw();
  }

  // 区块拖拽
  blockDragHandler(e) {
  }

  pointClickHandler(e) {
    if (e.evt.button === 0 && this.view === VIEW.SELECTION) {
      this.changeTransformNodes(e.currentTarget);
      this.render();
      const data = this.graphicList.find(i => i.id === e.currentTarget.id());
      if (this.curShape && this.curShape.attrs && this.curShape.attrs.properties && this.curShape.attrs.properties.parking_no) {
        data.property.name = this.curShape.attrs.properties.parking_no;
      }
      this.payload.listeners.pointClick(
        data.toObject(),
        'graphic',
        this.pointDict[e.currentTarget.id()],
      );
    }
  }

  // 块点击
  blockClickHandler(e) {
    if (e.evt.button === 0 && this.view === VIEW.SELECTION) {
      this.changeTransformNodes(e.currentTarget);
      this.render();

      this.payload.listeners.blockClick(
        this.graphicList.find(i => i.id === e.currentTarget.id()).toObject(),
        'graphic',
      );
    }
  }

  poiClickHandler(e, poi) {
    if (e.evt.button === 0) {
      this.changeTransformNodes(e.currentTarget);
      this.render();

      this.payload.listeners.pointClick(poi, 'poi');
    }
  }

  async initSvg() {
    const { svgPath, svgAlignParam, shapes } = this.payload;
    const svgObjects = shapes || await util.loadSVGFromURL(svgPath);
    // console.log('------initSvg', svgObjects);
    // 地图块
    const s = [];
    // 块级点
    const t = [];
    svgObjects.forEach((obj) => {
      if (obj.ispoi == '1') {
        t.push(obj);
      } else {
        s.push(obj);
      }
    });

    s.forEach((obj) => {
      this.drawSvgObject(obj);
    });

    t.forEach((obj) => {
      const point = new Point(obj.name, obj.cx, obj.cy);

      this.pointDict[point.id] = point;
      const property = { type: '0' };

      this.addGraphic('blockPoint', [point.id], property, point.id, 'BlockPoint', point.uuid);
    });

    this.svgObjects = s;

    this.checkBlockData();
    this.svgLayer.batchDraw();
  }

  checkBlockData() {
    const data = this.svgObjects;
    const s = {};
    let hasEmptyBlock = false;
    data.forEach((b) => {
      if (!b.name && b.isStore) {
        b.fill('red');
        hasEmptyBlock = true;
      }
      if (b.name) {
        if (s[b.name]) {
          s[b.name] += 1;
        } else {
          s[b.name] = 1;
        }
      }
    });
    if (hasEmptyBlock) {
      this.payload.listeners.errorHandler('有店铺未绑定blockId');
      return;
    }
    const resArr = [];
    Object.keys(s).forEach((i) => {
      if (s[i] > 1) {
        resArr.push(i);
      }
    });
    if (resArr.length) {
      if (this.payload.listeners.errorHandler) {
        this.payload.listeners.errorHandler(`检查到多个blockId【${resArr.join(',')}】,请检查C端地图`);
      }
    }
  }

  // 绘制地图块
  drawSvgObject(obj) {
    const path = new Konva.Path({
      data: obj.d,
      shape: obj.s,
      fill: obj.fill,
      stroke: obj.stroke !== 'none' ? obj.stroke : null,
      strokeWidth: parseInt(obj['stroke-width'], 10),
      name: obj.name,
    });
    if (obj.properties) {
      path.setAttr('properties', JSON.parse(obj.properties));
    }

    path.on('click', (e) => {
      if (e.evt.button === 0) {
        this.changeTransformNodes(e.currentTarget);
        this.render();

        this.payload.listeners.pointClick(path.attrs, 'block');
      }
    });
    this.svgShapes.push(path);
    this.svgLayer.add(path);

    if (path.attrs && path.attrs.properties && path.attrs.properties.parking_no) {
      // 计算包中心点
      const ext = [Infinity, Infinity, -Infinity, -Infinity];
      path.dataArray.forEach((data) => {
        const p = data.points;
        if (p.length) {
          ext[0] = Math.min(ext[0], p[0]);
          ext[1] = Math.min(ext[1], p[1]);
          ext[2] = Math.max(ext[2], p[0]);
          ext[3] = Math.max(ext[3], p[1]);
        }
      });
      const cx = (ext[0] + ext[2]) * 0.5;
      const cy = (ext[1] + ext[3]) * 0.5;
      const complexText = new Konva.Text({
        x: cx,
        y: cy,
        text: path.attrs.properties.parking_no,
        fontSize: 6,
        fill: '#555',
        width: 30,
        height: 6,
      });
      complexText.x(cx - complexText.textWidth / 2);
      complexText.y(cy - complexText.textHeight / 2);
      this.textLayer.add(complexText);
    }
  }

  deleteGraphic(id) {
    const graphicList = this.graphicList.filter(g => g.id === id);
    if (!graphicList) return;

    graphicList.forEach((g) => {
      g.ele.destroy();
      const gIndex = this.graphicList.findIndex(l => l.id === id);
      this.graphicList.splice(gIndex, 1);

      // 如果是点，那么把连线删掉
      if (g.type === 'point') {
        const lines = this.graphicList.filter(l => l.type === 'line' && l.data.indexOf(g.data[0]) > -1);
        lines.forEach((l) => {
          l.ele.destroy();
          const lIndex = this.graphicList.findIndex(i => i.id === l.id);
          this.graphicList.splice(lIndex, 1);
        });
      }
    });

    this.changeTransformNodes(null);
    this.render();
  }

  changeView(view) {
    if (view === this.view) return;
    console.log('changeViewchangeView');
    if (this.prevLine) {
      this.prevLine.destroy();
    }
    this.prevLine = null;
    this.prevPoint = null;
    this.curPoints = null;
    this.updateCurShape(null);
    if (view === VIEW.SELECTION) {
      this.graphicList.forEach((g) => {
        if (g.canDraggable) {
          g.ele.draggable(true);
        }
      });
      this.mousePoint.hide();
    } else {
      this.graphicList.forEach((g) => {
        if (g.canDraggable) {
          g.ele.draggable(false);
        }
      });
      this.mousePoint.show();
    }

    if (view === VIEW.SELFAREA) {
      this.areaLayer.show();
    }

    this.payload.listeners.changeView(view);
    this.view = view;
    this.render();
  }

  // 图层显示隐藏
  layerVisible(key, value) {
    if (!value) {
      this[key].hide();
    } else {
      this[key].show();
    }
    this[key].batchDraw();
  }

  // 图层显示隐藏
  elementVisible(key, value) {
    console.log(key);
    this.path_direction = true;
    console.log(value);
    console.log(this.graphicList);
    this.graphicList.forEach((e) => {
      if (e.type === 'line' && e.property && e.property.path_direction) {
        console.log('line');
        if (value) {
          e.ele.show();
        } else {
          e.ele.hide();
        }
      }
    });
    this.lineLayer.batchDraw();
  }

  clearPois() {
    if (!this.pois) return;
    this.pois.forEach((poi) => {
      poi.destroy();
    });

    this.render();
  }

  loadPois(pois) {
    this.pois = [];
    pois.forEach((poi) => {
      const kText = new Konva.Text({
        x: poi.position.x,
        y: poi.position.y - 10,
        text: poi.labelText,
        align: 'center',
        fontSize: 12,
        offsetX: -poi.labelOffset.x,
        offsetY: -poi.labelOffset.y,
      });
      kText.offsetX(kText.width() / 2);
      const poiGroup = new Konva.Group();

      if (poi.iconImage) {
        const image = new Image();
        image.src = poi.iconImage;
        const kPoi = new Konva.Image({
          x: poi.position.x,
          y: poi.position.y,
          image,
          width: poi.iconSize.width,
          height: poi.iconSize.height,
          offsetX: -poi.iconOffset.x + poi.iconSize.width / 2,
          offsetY: -poi.iconOffset.y + poi.iconSize.height / 2,
        });
        image.onload = () => {
          this.svgLayer.batchDraw();
        };
        kText.y(poi.position.y + 15);
        poiGroup.add(kPoi);
      }

      poiGroup.add(kText);
      poiGroup.on('click', (e) => {
        this.poiClickHandler(e, poi);
      });

      this.pois.push(poiGroup);
      this.svgLayer.add(poiGroup);
      this.svgLayer.batchDraw();
    });
  }

  exportData(data = {}) {
    const {
      pointDict,
      graphicList,
    } = data;
    const pointData = pointDict || this.pointDict;
    const graphicData = graphicList || this.graphicList;
    const point_dict = {};
    Object.entries(pointData).forEach((key) => {
      point_dict[key[0]] = {
        uuid: key[1].uuid,
        x: key[1].x,
        y: key[1].y,
      };
    });
    const graphic_list = [];
    graphicData.forEach((g) => {
      graphic_list.push(g.toObject());
    });
    return _.cloneDeep({
      point_dict,
      graphic_list,
      image_url: '',
    });
  }

  exportBlockData() {
    const res = [];
    this.svgObjects.forEach((obj) => {
      if (!obj.s.length) return;
      res.push({
        graphic_id: obj.name,
        center_x_2d: obj.s[0].centroid[0],
        center_y_2d: obj.s[0].centroid[1],
      });
    });
    return res;
  }

  resize() {
    this.stage.size({
      width: this.payload.container.clientWidth,
      height: this.payload.container.clientHeight,
    });
  }

  dispose() {
    // 键盘
    window.removeEventListener('keydown', this.events.keyboardHandler);
    this.payload = null;
    this.stage.destroy();
  }
}

export {
  KonvaStage,
  VIEW,
};
